'use client';

import { Icon } from '@/general/Icons/Icon';
import clsx from 'clsx';
import { ChangeEventHandler, FC, ReactNode, useEffect, useRef } from 'react';

import { ButtonIcon } from '@/general/components/Button/ButtonIcon';

import { getInputAria } from '../Input.helpers';
import { InputBaseWithOptions, InputGroupValue, InputOption } from '../Input.type';

type Props = Prettify<
  Omit<InputBaseWithOptions<InputGroupValue>, 'options' | 'onChange' | 'variant'> & {
    type: 'radio' | 'checkbox' | 'switch';
    option: InputOption;
    onChange: ChangeEventHandler<HTMLInputElement>;
    onBlur?: () => void;
    onDelete?: () => void;
    inputIcon: React.FC<{
      indeterminate?: boolean;
      disabled?: boolean;
      error?: ReactNode;
      checked: boolean;
    }>;
    variant: 'isolated' | 'list' | 'group' | 'group-deletable';
  }
>;

/** Component representing either a checkbox or a radio */
export const ControlInputOption: FC<Props> = ({
  type,
  option,
  onChange,
  onBlur,
  onDelete,
  variant = 'isolated',
  inputPlacement = 'start',
  inputIcon,
  ...props
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const checked = isChecked(option.value, props.value);
  const disabled = option.disabled ?? props.disabled;
  const hidden = option.hidden ?? false;
  const InputComponent = inputIcon;
  const inputType = type === 'checkbox' || type === 'switch' ? 'checkbox' : 'radio';

  // For group-deletable variant, always use start placement
  const effectiveInputPlacement = variant === 'group-deletable' ? 'start' : inputPlacement;
  const effectiveVariant = variant === 'group-deletable' ? 'group' : variant;

  // Handles the indeterminate status of the checkbox.
  useEffect(() => {
    if (type === 'checkbox' && inputRef.current && option.indeterminate !== undefined) {
      inputRef.current.indeterminate = option.indeterminate;
    }
  }, [option.indeterminate, type]);

  const handleDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onDelete?.();
  };

  const hasContent = !!option.content;

  return (
    <label
      className={clsx(
        'ControlInputOption',
        effectiveVariant,
        variant === 'group-deletable' && 'group-deletable',
        inputType,
        effectiveInputPlacement,
        {
          checked: checked,
          indeterminate: option.indeterminate,
          disabled: disabled,
          error: !!props.error,
          hidden: hidden,
        },
        props.inputClassName,
        'labelWrapper',
      )}
      data-testid={
        option['data-testid'] || (props['data-testid'] ? `${props['data-testid']}-${option.value}` : undefined)
      }
    >
      {hasContent && checked ? (
        <div className="option-wrapper">
          <div className="option-label-wrapper">
            <div className="inputWrapper">
              <input
                type={inputType}
                name={props.name}
                value={option.value}
                disabled={disabled}
                className="input"
                {...getInputAria(props)}
                checked={checked}
                ref={inputRef}
                onChange={onChange}
                onBlur={onBlur}
              />
              <InputComponent {...option} error={props.error} checked={checked} disabled={disabled} />
            </div>
            <div className={clsx('content', 'label')}>{option.label}</div>
          </div>
          <div className="mx-2 border-t border-neutral-400" />
          <div className="option-content">{option.content}</div>
        </div>
      ) : (
        <>
          <div className="inputWrapper">
            <input
              type={inputType}
              name={props.name}
              value={option.value}
              disabled={disabled}
              className="input"
              {...getInputAria(props)}
              checked={checked}
              ref={inputRef}
              onChange={onChange}
              onBlur={onBlur}
            />
            <InputComponent {...option} error={props.error} checked={checked} disabled={disabled} />
          </div>
          <div className={clsx('content', 'label')}>{option.label}</div>
        </>
      )}
      {variant === 'group-deletable' && onDelete && (
        <div className="deleteButton">
          <ButtonIcon
            icon={<Icon name="trash" />}
            onClick={handleDelete}
            variant="tertiary"
            size="small"
            title="Delete"
            disabled={disabled}
            data-testid={props['data-testid'] ? `${props['data-testid']}-delete-${option.value}` : undefined}
          />
        </div>
      )}
    </label>
  );
};

const isChecked = (value: string, inputValue: InputGroupValue | undefined): boolean =>
  (Array.isArray(inputValue) && inputValue.indexOf(value) >= 0) || value === inputValue;
